/** Javascript & React helper methods - Copyright (c) Cupla Software */

import React from "react";

export function toRadians(deg: number) {
    return (deg * Math.PI) / 180;
}

export function toDegrees(rad: number) {
    return (rad / Math.PI) * 180;
}

/** Gets display rect */
export function getDisplayRect(): DOMRectReadOnly {
    return document.getElementById("display").getBoundingClientRect();
}

/** Gets element rect relative to display */
export function getElementDisplayRect(elem: HTMLElement): DOMRectReadOnly {
    let rd = getDisplayRect();
    let r = elem.getBoundingClientRect();
    return DOMRectReadOnly.fromRect({
        x: r.x - rd.x,
        y: r.y - rd.y,
        width: r.width,
        height: r.height
    });
}

/** Gets element rect relative to its parent */
export function getElementRect(
    elem: HTMLElement,
    inScrollArea?: boolean
): DOMRectReadOnly {
    let parent = elem.parentNode as HTMLElement;
    let r = elem.getBoundingClientRect();
    let rp = parent ? parent.getBoundingClientRect() : r;
    let sx = 0;
    let sy = 0;
    if (inScrollArea) {
        sx = parent.scrollLeft;
        sy = parent.scrollTop;
    }
    return DOMRectReadOnly.fromRect({
        x: r.x - rp.x + sx,
        y: r.y - rp.y + sy,
        width: r.width,
        height: r.height
    });
}

export function div() {
    return document.createElement("div");
}

export function removeAllChildren(elem: HTMLElement) {
    while (elem.lastChild) {
        elem.removeChild(elem.lastChild);
    }
}

export function post(callback: (args: void) => void, ms?: number): Timeout {
    return setTimeout(callback, ms);
}

export function postAt(
    callback: (args: void) => void,
    time: Date | Timestamp
): Timeout {
    if (time instanceof Date) {
        time = time.getTime();
    }
    return setTimeout(callback, time - Date.now());
}

/** Gets current time as epoch (ms) */
export function currentTime(): Timestamp {
    return Date.now();
}

let nextKey = 1;

/** Returns unique number which can be used as React element key */
export function key(): number {
    return nextKey++;
}

export function nbsp(s?: string) {
    return s ? s : "\u00A0";
}

export function url(s: string) {
    return "url(" + s + ")";
}

export function padzero(s: any, n: number): string {
    s = String(s);
    while (s.length < n) {
        s = "0" + s;
    }
    return s;
}

export function repeat<T>(n: T, count: number): T[] {
    let arr: T[] = [];
    for (let i = 0; i < count; i++) {
        arr.push(n);
    }
    return arr;
}

export function hex(n: number): string {
    let s = Number(n).toString(16);
    if (s.length < 2) {
        s = "0" + s;
    }
    return s;
}

/** Converts base of large number string. For example convertNumberBase("f",16,10)=="15" */
export function convertNumberBase(
    str: string,
    fromBase: number,
    toBase: number
): string {
    let sum = [];
    let num = str.split("");
    while (num.length) {
        let s = parseInt(num.shift(), fromBase);
        for (let i = 0; s || i < sum.length; i++) {
            s += (sum[i] || 0) * fromBase;
            sum[i] = s % toBase;
            s = (s - sum[i]) / toBase;
        }
    }
    let out = [];
    while (sum.length) {
        out.push(sum.pop().toString(toBase));
    }
    return out.join("");
}

export function isValid(n: number): boolean {
    return typeof n !== "undefined" && !isNaN(n);
}

export function isInfinity(n: number): boolean {
    return typeof n === "number" && !isNaN(n) && !isFinite(n);
}

export function parseValidNumber(n: any, def?: number): number {
    return typeof n !== "undefined" && !isNaN(n) && n !== null
        ? Number(n)
        : typeof def !== "undefined"
        ? def
        : 0;
}

export function parseValidBoolean(n: any, def?: boolean): boolean {
    return typeof n !== "undefined" && !isNaN(n) && n !== null
        ? Boolean(n)
        : typeof def !== "undefined"
        ? def
        : false;
}

export function parseValidString(s: any, def?: string): string {
    return typeof s !== "undefined" && s !== null
        ? String(s)
        : typeof def !== "undefined"
        ? def
        : "";
}

/** Gets random integer in range [lo,hi]. NOTE hi inclusive. */
export function random(lo: number, hi: number): number {
    return lo + Math.floor(Math.random() * (hi - lo + 1));
}

/** Maps value n from range [a,b] to range [c,d] */
export function mapvalue(
    n: number,
    a: number,
    b: number,
    c: number,
    d: number,
    enforce?: boolean
): number {
    if (b === a) {
        // Source range is empty => stupid to call this way, returning always max (the "100%" case)
        return d;
    }

    // Map value (works even if ranges are negative or value goes out of the range)
    n = c + ((n - a) / (b - a)) * (d - c);

    // Enforce value inside target range
    if (enforce) {
        if (d >= c) n = clamp(c, n, d);
        else n = clamp(d, n, c);
    }

    return n;
}

export function clamp(mina: number, a: number, maxa: number): number {
    return a < mina ? mina : a > maxa ? maxa : a;
}

export function notnull<T>(n: T): T {
    return n ? n : null;
}

export function getByID<T extends { id: number | string }>(
    array: T[],
    id: number | string
): T {
    return notnull(array.find(elem => elem.id === id));
}

export function getBy<T, V>(array: T[], field: keyof T, value: V): T {
    return notnull(array.find(elem => elem[field] === value));
}

export function remove<T>(array: T[], elem: T): boolean {
    let i = array.indexOf(elem);
    if (i >= 0) {
        array.splice(i, 1);
        return true;
    }
    return false;
}

//! DateEx

/** Gets date week number */
export function getDateWeekNumber(date: Date) {
    date = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    let yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    return Math.ceil(
        ((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
    );
}

/** Gets date weekday string */
export function getDateWeekdayString(date: Date | Timestamp, short?: boolean) {
    if (!(date instanceof Date)) {
        date = new Date(date);
    }
    let n = date.getDay();
    let s = "";
    if (n === 0) {
        s = "Sunday";
    } else if (n === 1) {
        s = "Monday";
    } else if (n === 2) {
        s = "Tuesday";
    } else if (n === 3) {
        s = "Wednesday";
    } else if (n === 4) {
        s = "Thursday";
    } else if (n === 5) {
        s = "Friday";
    } else if (n === 6) {
        s = "Saturday";
    }
    return short ? s.substring(0, 3) : s;
}

export function getDateMonthString(date: Date | Timestamp, short?: boolean) {
    if (!(date instanceof Date)) {
        date = new Date(date);
    }
    let n = date.getMonth() + 1;
    let s = "";
    if (n === 1) {
        s = "January";
    } else if (n === 2) {
        s = "February";
    } else if (n === 3) {
        s = "March";
    } else if (n === 4) {
        s = "April";
    } else if (n === 5) {
        s = "May";
    } else if (n === 6) {
        s = "June";
    } else if (n === 7) {
        s = "July";
    } else if (n === 8) {
        s = "August";
    } else if (n === 9) {
        s = "September";
    } else if (n === 10) {
        s = "October";
    } else if (n === 11) {
        s = "November";
    } else if (n === 12) {
        s = "December";
    }
    return short ? s.substring(0, 3) : s;
}

/** Gets date as visual local time string */
export function getDateString(date: Date | Timestamp) {
    if (!date) {
        return "";
    }
    if (!(date instanceof Date)) {
        date = new Date(date);
    }
    return (
        date.getDate() + "." + (date.getMonth() + 1) + "." + date.getFullYear()
    );
}

/** Gets date+time as visual local time string */
export function getDateTimeString(
    date: Date | Timestamp,
    includeMilliseconds?: boolean
) {
    if (!date) {
        return "";
    }
    if (!(date instanceof Date)) {
        date = new Date(date);
    }
    return (
        date.getDate() +
        "." +
        (date.getMonth() + 1) +
        "." +
        date.getFullYear() +
        " " +
        padzero(date.getHours(), 2) +
        ":" +
        padzero(date.getMinutes(), 2) +
        ":" +
        padzero(date.getSeconds(), 2) +
        (includeMilliseconds ? "." + padzero(date.getMilliseconds(), 3) : "")
    );
}

/** Gets time as visual local time string */
export function getTimeString(
    date: Date | Timestamp,
    includeMilliseconds?: boolean
) {
    if (!date) {
        return "";
    }
    if (!(date instanceof Date)) {
        date = new Date(date);
    }
    return (
        padzero(date.getHours(), 2) +
        ":" +
        padzero(date.getMinutes(), 2) +
        ":" +
        padzero(date.getSeconds(), 2) +
        (includeMilliseconds ? "." + padzero(date.getMilliseconds(), 3) : "")
    );
}

/** Parses local time string */
export function parseDateTimeString(str: string) {
    // "0000-00-00 00:00:00" or "0000-00-00 00:00"
    str = str.trim();
    let ds = str.split(/\D/).map(s => parseInt(s));
    if (ds.length < 5) {
        if ((ds.length === 2 || ds.length === 3) && ds[0] <= 23) {
            // "00:00:00" or "00:00"
            let now = new Date();
            ds.splice(
                0,
                0,
                now.getFullYear(),
                now.getMonth() + 1,
                now.getDate()
            );
        } else if (ds.length === 3 && ds[0] >= 1900) {
            // "0000-00-00"
        } else {
            return null;
        }
    }
    ds[1] = ds[1] - 1; // month
    return new Date(...(ds as []));
}

export function sortFunction<T>(field: keyof T) {
    return (a: any, b: any) => compare(a[field], b[field]);
}

export function sortFunctionNumber<T>(field: keyof T) {
    return (a: any, b: any) =>
        compare(parseInt(a[field]), parseInt(b[field]), true);
}

/** Sorts array (T) according value (V) which is created by mapping function.
 *  Mapping is done for each element only once, so this is useful when mapping is heavy.
 */
export function sortMap<T, V extends number | string | boolean>(
    arr: T[],
    mapFunc: (elem: T) => V
) {
    return arr
        .map(elem => ({
            elem,
            value: mapFunc(elem)
        }))
        .sort((a, b) => compare(a.value, b.value))
        .map(e => e.elem);
}

export function compare<T extends number | string | boolean>(
    a: T,
    b: T,
    nan?: boolean
) {
    if (nan && (isNaN(a as number) || isNaN(b as number))) {
        if (isNaN(a as number) && !isNaN(b as number)) {
            return -1;
        }
        if (!isNaN(a as number) && isNaN(b as number)) {
            return +1;
        }
        return 0;
    }
    if (a < b) {
        return -1;
    }
    if (a > b) {
        return +1;
    }
    return 0;
}

/** React holder class */
export class ReactHolder<T extends React.Component | HTMLElement = any> {
    /** React element */
    element: React.ReactElement;

    /** Instance of created component or HTMLElement */
    instance: T;

    /** Constructs holder from React Component or HTMLElement
     *    ReactHolder(<MyComponent/>)
     *    ReactHolder(React.createElement(...))
     *    ReactHolder(<div/>)
     */
    constructor(
        reactElement: React.ReactElement,
        ref?: (e: any) => void,
        key?: React.Key
    ) {
        this.element = React.cloneElement(reactElement, {
            ref: (e: any) => {
                this.instance = e;
                if (ref) {
                    ref(e);
                }
            },
            key /* does not overwrite if undefined */
        });
    }

    render(): React.ReactElement {
        return this.element;
    }
}

/** Asynchronous sleep */
export async function sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function exportTableCSV(table: any) {
    let csv = "";
    for (let row of table.rows) {
        for (let cell of row.cells) {
            let v = "";
            if (
                cell.children.length &&
                cell.children[0] instanceof HTMLInputElement
            ) {
                v = cell.children[0].value.toString();
            } else {
                v = cell.innerText;
            }
            csv += v.replace(/\./, ",") + ";";
        }
        csv += "\n";
    }
    return csv;
}

/** Reads BLOB (or file)
 *  readBlob(blob), readBlob(file), readBlob(new Blob([data]))
 *  type:
 *    data  => reads binary and returns string as application/octet-stream,base64
 *    array => reads binary and returns array buffer
 *    text  => reads text and returns string
 */
export async function readBlob(blob: Blob, type: string) {
    return new Promise(resolve => {
        let reader = new FileReader();
        reader.onload = e => {
            resolve(e.target.result);
        };
        reader.onerror = () => resolve(null);
        if (type === "data") {
            reader.readAsDataURL(blob);
        } else if (type === "array") {
            reader.readAsArrayBuffer(blob);
        } else if (type === "text") {
            reader.readAsText(blob);
        }
    });
}

/** Offers user to save file with the specified data as content */
export function offerFile(filename: string, data: any, isUTF8?: boolean) {
    let a = document.createElement("a");
    let url = URL.createObjectURL(new Blob(isUTF8 ? ["\ufeff", data] : [data]));
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export function mean(numbers: number[]) {
    let a = numbers.filter(n => !isNaN(n));
    if (a.length === 0) {
        return NaN;
    } else if (a.length === 1) {
        return a[0];
    }
    let sum = 0;
    a.forEach(n => (sum += n));
    return sum / a.length;
}

export function median(numbers: number[]) {
    let a = numbers.filter(n => !isNaN(n)).sort((a, b) => a - b);
    if (a.length === 0) {
        return NaN;
    } else if (a.length === 1) {
        return a[0];
    }
    let i = Math.floor(a.length / 2);
    if (a.length % 2 === 0) {
        return (a[i - 1] + a[i]) / 2;
    } else {
        return a[i];
    }
}

export class Time {
    static Second = 1000;
    static Minute = 60 * 1000;
    static Hour = 60 * 60 * 1000;
    static Day = 24 * 60 * 60 * 1000;
    static Week = 7 * 24 * 60 * 60 * 1000;
}
